import React, { useMemo } from 'react';
import { first, last } from 'lodash';
import moment from 'moment';
import { useCalendarDropdown } from '../../calendar-dropdown/useCalendarDropdown';
import {
  type AsOfDateSetting,
  getAllocationsAsOfDateTimestampFromSetting,
  serializeAsOfDateSetting,
  UNSAFE_deserializeAsOfDateSetting,
} from 'venn-utils';
import { assertExhaustive } from 'venn-utils';
import { CalendarDropdownPrimitives } from '../../calendar-dropdown/CalendarDropdownPrimitives';
import {
  AllocationAsOfDateDropdownContext,
  type AllocationAsOfDateDropdownContextValue,
} from './AllocationAsOfDateDropdownContext';
import { ThirtyDayCalendarPicker } from './thirty-day-calendar-picker/ThirtyDayCalendarPicker';

type AllocationAsOfDateDropdownProps = {
  allocationsAsOfDate: AsOfDateSetting;
  lastUploadedAllocationsDate: number;
  currentDate: number;
  allocationDates: number[];
  setAllocationsAsOfDate: (asOfDate: AsOfDateSetting) => void;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'UNKNOWN' | undefined;
  triggerSize: 'small' | 'normal';
  disabled?: boolean;
};

export const AllocationAsOfDateDropdown = ({
  allocationsAsOfDate,
  lastUploadedAllocationsDate,
  allocationDates,
  setAllocationsAsOfDate,
  frequency,
  currentDate,
  triggerSize,
  disabled = false,
}: AllocationAsOfDateDropdownProps) => {
  const start = first(allocationDates);
  const end = last(allocationDates);
  const { selectedDate, onValueChange, begin, ending, formattedDisplayDate, lastUpdatedValue } = useCalendarDropdown({
    value: allocationsAsOfDate,
    lastUpdated: moment.utc(lastUploadedAllocationsDate).toDate(),
    currentDate: moment.utc(currentDate).toDate(),
    start: moment.utc(start).toDate(),
    end: moment.utc(end).toDate(),
    onChange: setAllocationsAsOfDate,
  });
  const asOfDateTimestamp = moment
    .utc(
      getAllocationsAsOfDateTimestampFromSetting({
        asOfDateSetting: UNSAFE_deserializeAsOfDateSetting(selectedDate),
        currentDate,
        lastUploadedDate: lastUploadedAllocationsDate,
      }),
    )
    .toDate()
    .getTime();
  const [isDropdownPanelOpen, setIsDropdownPanelOpen] = React.useState(false);
  const allocationAsOfDateDropdownContext: AllocationAsOfDateDropdownContextValue = useMemo(
    () => ({
      mutations: {
        setAllocationsAsOfDate: (asOfDate) => {
          onValueChange(serializeAsOfDateSetting(asOfDate));
        },
        closeDropdownPanel: () => setIsDropdownPanelOpen(false),
      },
    }),
    [setIsDropdownPanelOpen, onValueChange],
  );
  const datePicker = useMemo(() => {
    switch (frequency) {
      case 'DAILY':
        return (
          <AllocationAsOfDateDropdownContext.Provider value={allocationAsOfDateDropdownContext}>
            <ThirtyDayCalendarPicker
              allocationsAsOfDate={allocationsAsOfDate}
              asOfDateTimestamp={asOfDateTimestamp}
              currentDate={currentDate}
              lastUploadedDate={lastUploadedAllocationsDate}
              allocationDates={allocationDates}
            />
          </AllocationAsOfDateDropdownContext.Provider>
        );
      case 'MONTHLY':
      case 'QUARTERLY':
        return (
          <CalendarDropdownPrimitives.AccordionDateContent
            end={moment.utc(end).toDate()}
            frequency={frequency}
            begin={begin}
            ending={ending}
            lastUpdatedValue={lastUpdatedValue}
            value={allocationsAsOfDate}
          />
        );
      case 'WEEKLY':
      case 'YEARLY':
      case 'UNKNOWN':
      case undefined:
        throw new Error(`Unsupported frequency: ${frequency}`);
      default:
        return assertExhaustive(frequency);
    }
  }, [
    allocationAsOfDateDropdownContext,
    allocationDates,
    allocationsAsOfDate,
    asOfDateTimestamp,
    begin,
    currentDate,
    end,
    ending,
    frequency,
    lastUpdatedValue,
    lastUploadedAllocationsDate,
  ]);
  const variant = frequency === 'DAILY' ? 'thirtyDayCalendar' : 'accordion';
  return (
    <CalendarDropdownPrimitives.Root
      selectedDate={selectedDate}
      onValueChange={onValueChange}
      disabled={disabled}
      open={isDropdownPanelOpen}
      onOpenChange={(b) => setIsDropdownPanelOpen(b)}
    >
      <CalendarDropdownPrimitives.Trigger formattedDisplayDate={formattedDisplayDate} size={triggerSize} />
      <CalendarDropdownPrimitives.ContentRoot variant={variant}>{datePicker}</CalendarDropdownPrimitives.ContentRoot>
    </CalendarDropdownPrimitives.Root>
  );
};
